import { studyQueryKeys } from '@/config/queryKeys';
import { useDateFormatter } from '@/hooks';
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import CreateForm from '@/pages/Studies/components/CreateForm.tsx';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app.ts';
import { PaginatedStudiesResponse } from '@/types';
import { Group, Loading, Popover, getTextClassName } from '@nautilusbio/nb-design-system';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Cog, Plus, RefreshCcw } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { STUDIES_QUERY_GC_TIME, STUDIES_QUERY_STALE_TIME, mainIconSize } from '../../constants';
import { StudiesContent } from '../../pages/Studies/components';
import styles from '../../pages/Studies/styles.module.css';

const studiesSearchSchema = z.object({
  openCreate: z.boolean().optional(),
});

export const Route = createFileRoute('/studies/')({
  beforeLoad: ({ context }) => {
    // Redirect to login if user is not authenticated
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({
        to: '/login',
      });
    }
  },
  component: RouteComponent,
  validateSearch: studiesSearchSchema,
});

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div role="alert" aria-describedby="error-heading error-message">
      <Text as="h2" id="error-heading">
        Something went wrong:
      </Text>
      <Text as="pre" id="error-message">
        {error.message}
      </Text>
      <Button aria-label="Retry loading studies" data-testid="error-refresh-btn" onPress={resetErrorBoundary}>
        Try again
      </Button>
    </div>
  );
}

function RouteComponent() {
  usePostLoginRedirect();

  const [showSettings, setShowSettings] = useState<boolean>(false);
  const { t } = useTranslation();
  const search = useSearch({ from: '/studies/' });
  const userOrg = useAppStore(useShallow(state => state.selectedOrganization));
  const formatDate = useDateFormatter();

  /**
   * Fetch studies data using React Query
   *
   * - Fetches all studies for both card and table view
   * - React Query automatically caches and updates data
   * - Mutations update this cache automatically
   * - Table view uses server-side sorting/filtering (see queryFn parameters)
   * - staleTime: 30 minutes - data is considered fresh for 30 minutes, no refetch on navigation
   * - gcTime: 60 minutes - cached data is kept in memory for 60 minutes after last use
   */
  const {
    data: studies,
    error,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery<PaginatedStudiesResponse>({
    enabled: !!userOrg,
    queryKey: userOrg ? studyQueryKeys.listSearch(userOrg.id) : ['disabled-studies-query'],
    queryFn: async () => {
      if (!userOrg) {
        throw new Error('Organization ID is required');
      }

      // Fetch all studies with default sorting
      return searchStudies(userOrg?.id, {
        skip: 0,
        limit: 500, // API maximum limit - orgs with 500+ studies will only see most recent 500
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },
    gcTime: STUDIES_QUERY_GC_TIME,
    refetchOnWindowFocus: 'always',
    refetchOnMount: 'always',
    retry: 2,
    staleTime: STUDIES_QUERY_STALE_TIME,
  });

  // Track last updated timestamp for studies data
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Update lastUpdated when studies data changes
  useEffect(() => {
    if (studies) {
      setLastUpdated(new Date());
    }
  }, [studies]);

  // Dialog open state
  const [createIsOpen, setCreateOpen] = useState(false);

  // Open dialog if openCreate flag is present in URL
  useEffect(() => {
    if (search.openCreate) {
      setCreateOpen(true);
    }
  }, [search.openCreate]);

  const dialogCloseHandler = useCallback(() => setCreateOpen(prev => !prev), []);

  if (error) {
    return <ErrorFallback error={error} resetErrorBoundary={refetch} />;
  }

  const createStudyTxt = t('study.create', { ns: 'create' });

  return (
    <Stack alignItems="start" className={styles.container} orientation="vertical">
      {!!userOrg && (
        <Stack
          alignItems="center"
          className={styles.newTitleRow}
          justifyContent="space-between"
          orientation="horizontal"
        >
          <Stack alignItems="center" orientation="horizontal" gap={2}>
            <Popover
              isOpen={showSettings}
              content={
                <Group orientation="vertical">
                  <Button
                    aria-label={t('refetch_tooltip', { ns: 'studies' })}
                    isDisabled={isLoading || isRefetching}
                    onPress={async () => {
                      const result = await refetch();
                      setShowSettings(false);
                      if (result.status === 'success') {
                        toast.success(t('refetch_success', { ns: 'studies' }));
                        setLastUpdated(new Date());
                      } else if (result.status === 'error') {
                        toast.error(t('refetch_error', { ns: 'studies' }));
                      }
                    }}
                    size="xs"
                    data-testid="studies-refresh-btn"
                    visual="primary"
                  >
                    {isRefetching ? (
                      <Loading className={styles.isRefetching} type="inline" />
                    ) : (
                      <RefreshCcw size={14} />
                    )}
                    {isLoading || isRefetching
                      ? t('refetching', { ns: 'studies' })
                      : t('refetch_tooltip', { ns: 'studies' })}
                  </Button>
                </Group>
              }
              onClickOutside={() => setShowSettings(false)}
              positions={['right']}
            >
              <Button data-testid={'studies-setting-btn'} onPress={() => setShowSettings(true)} size="xs">
                <Cog size={14} />
              </Button>
            </Popover>
          </Stack>

          <Dialog
            content={<CreateForm onSuccess={dialogCloseHandler} userOrg={userOrg?.id} />}
            data-testid={'create-new-study-btn'}
            floatingProps={{
              open: createIsOpen,
              onOpenChange: setCreateOpen,
            }}
            onOpenChange={setCreateOpen}
            title={createStudyTxt}
            triggerProps={{
              children: (
                <>
                  <Plus size={mainIconSize} />
                  {createStudyTxt}
                </>
              ),
              size: 'md',
              visual: 'unstyled',
            }}
          />
        </Stack>
      )}

      {userOrg && (
        <>
          <StudiesContent isLoading={isLoading} studies={studies || null} />

          {lastUpdated && (
            <div className={clsx(styles.footer, getTextClassName('xs2'))}>
              <Text as="span">{t('last_updated', { ns: 'studies' }).replace('{{date}}', formatDate(lastUpdated))}</Text>
            </div>
          )}
        </>
      )}
    </Stack>
  );
}
